<template>
  <v-app>
    <v-app-bar 
      app 
      flat 
      color="white" 
      height="60px" 
      class='pt-2 mb-6' 
      :extension-height="form.status ? '30px' : '0px'"
      style="position: sticky; top: 0; z-index: 1000;"
    >
      <!-- ヘッダー（1行目）：日付切り替え -->
      <div style="width: 100%;" @click="openDateDialog">
        <DatePager :date="date" @addDay="addDay" @subtractDay="subtractDay" />
      </div>

      <!-- ヘッダー（2行目）：extension スロット -->
      <template v-slot:extension>
        <v-container fluid class="pa-0">
          <div class="d-flex align-center justify-space-between px-2 fill-height">
            <!-- 左端のステータスチップ -->
            <div>
              <v-chip dense small outlined color="gray" class="mb-4" v-if="form.status === 'draft'">
                {{ $t('diary.draft') }}
              </v-chip>
              <v-chip dense small outlined color="secondary" class="mb-4" v-if="form.status === 'applied'">
                {{ $t('diary.applied') }}
              </v-chip>
              <v-chip dense small outlined color="primary" class="mb-4" v-if="form.status === 'approved'">
                {{ $t('diary.approved') }}
              </v-chip>
            </div>

            <!-- 右端の承認者のバッジ -->
            <div>
              <v-chip
                dense
                small
                color="primary"
                class="mb-4"
                v-if="form.status === 'approved' && approverRole === 'chief'"
                text-color="white"
              >
                <v-icon left small>mdi-check-circle</v-icon>
                {{ $t('diary.chief') }}
              </v-chip>
            </div>
            <div>
              <v-chip
                dense
                small
                color="primary"
                class="mb-4"
                v-if="form.status === 'approved' && approverRole === 'director'"
                text-color="white"
              >
                <v-icon left small>mdi-check-circle</v-icon>
                {{ $t('diary.director') }}
              </v-chip>
            </div>
          </div>
        </v-container>
      </template>
    </v-app-bar>

    <div v-if="isLoading">
      <!-- ローダー -->
      <VueLoading :height="'50vh'" />
    </div>
    <div v-else>
      <!-- メインコンテンツ -->
      <v-main class='pa-1'>
        <v-container fluid class="py-4">
          <!-- 活動内容 -->
          <v-textarea
            v-model="form.activity"
            :label="$t('diary.activity')"
            variant="outlined"
            auto-grow
            class="mb-2"
          />

          <!-- 引き継ぎ・伝達事項 -->
          <v-textarea
            v-model="form.message"
            :label="$t('diary.handover')"
            variant="outlined"
            auto-grow
            class="mb-2"
          />

          <!-- 備考 -->
          <v-textarea
            v-model="form.remarks"
            :label="$t('diary.remarks')"
            variant="outlined"
            auto-grow
            class="mb-2"
          />

          <!-- 担当者 -->
          <v-select
            v-model="form.staff"
            :items="staffList"
            item-title="text"
            item-value="value"
            :label="$t('diary.staff')"
            variant="outlined"
            multiple
            chips
          />

          <!-- 申請者（一度決定されたら変更不可） -->
          <v-select
            v-model="form.appliedBy"
            :items="staffList"
            item-title="text"
            item-value="value"
            :label="$t('diary.applicant')"
            variant="outlined"
            :disabled='form.status === "draft" || form.status === "applied" || form.status === "approved"'
          />

          <!-- 承認者 -->
          <v-select
            v-if='!!form.approvedBy'
            v-model="form.approvedBy"
            :items="staffList"
            item-title="text"
            item-value="value"
            :label="$t('diary.approver')"
            variant="outlined"
            :disabled='!!form.approvedBy'
          />

          <!-- ボタン群 -->
          <v-row dense>
            <v-col cols="6" v-if='!form.status || form.status === "draft"'>
              <v-btn block variant="outlined" @click="saveDraft">{{ $t('diary.saveDraft') }}</v-btn>
            </v-col>
            <v-col cols="6" v-if='!form.status || form.status === "draft"'>
              <v-btn block color="primary" variant="flat" @click="saveAndApply">{{ $t('diary.saveAndApply') }}</v-btn>
            </v-col>
            <v-col cols="6" v-if='form.status === "applied"'>
              <v-btn block variant="outlined" color="error" @click="changeApprovalStatus('rejected')">{{ $t('diary.reject') }}</v-btn>
            </v-col>
            <v-col cols="6" v-if='form.status === "applied"'>
              <v-btn block color="primary" variant="flat" @click="changeApprovalStatus('approved')">{{ $t('diary.approve') }}</v-btn>
            </v-col>
            <v-col cols="9" v-if='form.status === "approved"'>
              <v-btn block color="error" variant="flat" @click="changeApprovalStatus('rejected')">{{ $t('diary.cancelApproval') }}</v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-main>
    </div>
    <v-dialog
      v-model="dateDialogFlg"
      width="auto"
      max-width="100%"
    >
      <v-card>
        <v-date-picker
          v-model="selectedDate"
          type="date"
          :day-format="(date) => new Date(date).getDate()"
          @update:model-value="dateSelected"
          no-title
          hide-header
          color="primary"
        >
        </v-date-picker>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script lang="ts">
//import Vue from 'vue';
import { defineComponent } from "vue";
import dayjs from 'dayjs';
import DatePager from '@/components/molecules/DatePager.vue';
import VueLoading from '@/components/pages/Loding.vue';
import {
  ApproveHoursDiaryRequest,
  ChangeHoursDiaryRequest,
  GetHoursDiaryRequest,
  PostHoursDiaryRequest,
} from '@/model/Request';
import { MeNurserySchoolResponse, MeResponse } from 'chaild-api/lib';
import LocalDataService from '@/service/LocalDataService';
import { nurseryModule } from '@/store/dataModules/nurseryModule';
import { hoursDiaryModule } from '@/store/dataModules/hoursDiaryModule';
import Swal from 'sweetalert2';

export default defineComponent({
  name: 'NurseryHoursDiaryContainer',
  components: { DatePager, VueLoading },
  data() {
    return {
      dateDialogFlg: false,
      selectedDate: dayjs().format('YYYY-MM-DD'),
      addflg: false,
      subtractflg: false,
      isLoading: true,
      date: dayjs(),
      form: {
        date: dayjs().format('YYYY-MM-DD'),
        activity: null,
        remarks: null,
        message: null,
        staff: [] as number[],
        appliedBy: null as number | null,
        approvedBy: null as number | null,
        status: 'draft' as string | null,
      },
      staffList: [] as { text: string; value: number }[],
      approverRole: null as 'chief' | 'director' | null,
    };
  },
  computed: {
    me(): MeResponse | null {
      return LocalDataService.getUser();
    },
    currentSchool(): MeNurserySchoolResponse | undefined {
      if (this.me) {
        let nurserySchool = this.me.nurserySchools.find(nursery => nursery.isDefault);
        if (this.me.nurserySchools.length === 0) {
          return undefined
        }
        if (!nurserySchool) {
          nurserySchool = this.me.nurserySchools[0];
        }
        return nurserySchool;
      }
      return undefined;
    },
    allStaff(): {userId: number; fullName: string;}[] {
      if (nurseryModule.allUser) {
        let allStaff = [] as any;
        nurseryModule.allUser.users.map(user => {
          allStaff.push({
            userId: user.userId,
            fullName: user.lastName + user.firstName,
          });
        });
        return allStaff;
      }
      return [];
    },
  },
  watch: {
    date: {
      immediate: true,
      handler() {
        this.loadDetail();
      }
    }
  },
  methods: {
    addDay() {
      this.date = this.date.add(1, 'day');
      sessionStorage.setItem('nurseryHoursDairyDate', this.date.format('YYYY-MM-DD'));
      this.addflg = true;
    },
    subtractDay() {
      this.date = this.date.subtract(1, 'day');
      sessionStorage.setItem('nurseryHoursDairyDate', this.date.format('YYYY-MM-DD'));
      this.subtractflg = true;
    },
    openDateDialog() {
      if (this.addflg || this.subtractflg) {
        this.addflg = false;
        this.subtractflg = false;
        return
      }
      this.selectedDate = this.date.format('YYYY-MM-DD');
      this.dateDialogFlg = true;
    },
    dateSelected(day) {
      this.date = dayjs(day);
      this.selectedDate = dayjs(day).format('YYYY-MM-DD');
      sessionStorage.setItem('nurseryHoursDairyDate', this.date.format('YYYY-MM-DD'));
      this.dateDialogFlg = false;
    },
    async loadDetail() {
      this.isLoading = true;

      this.form.status = null;
      this.form.date = this.date.format('YYYY-MM-DD');
      this.form.activity = null;
      this.form.remarks = null;
      this.form.message = null;
      this.form.approvedBy = null;
      this.form.appliedBy = null;

      try {
        await nurseryModule.getNurseryUser({
          nurseryId: this.currentSchool?.nurseryId ?? -1,
          date: this.form.date,
        });
        this.staffList = this.allStaff.map(u => ({
          text: u.fullName,
          value: u.userId,
        }));

        const req: GetHoursDiaryRequest = {
          nurseryId: this.currentSchool?.nurseryId ?? -1,
          date: this.form.date
        };
        const d = await hoursDiaryModule.getHoursDiary(req);
        const chief = d.approvedBy?.chief === -1 ? undefined : d.approvedBy?.chief;
        const director = d.approvedBy?.director === -1 ? undefined : d.approvedBy?.director;
        this.approverRole = director ? 'director' : chief ? 'chief' : null;
        const approvedBy = chief?.userNurseryId ?? director?.userNurseryId;
        // NOTE: 承認してもAPIのstatus="applied"は変わらないので、ここで調整する
        const status = approvedBy != null ? 'approved' : d.status;
        Object.assign(this.form, {
          activity: d.activity,
          remarks: d.remarks,
          message: d.message,
          staff: d.staff?.map(s => s.userNurseryId) || [],
          appliedBy: d.createdBy?.userNurseryId,
          approvedBy,
          status,
        });
      } finally {
        this.isLoading = false;
      }
    },
    async saveDraft() {
      this.isLoading = true;
      this.form.status = null;
      try {
        if (hoursDiaryModule.diaryDetail?.nurseryHoursDiaryId) {
          const req: ChangeHoursDiaryRequest = {
            nurseryId: this.currentSchool?.nurseryId ?? -1,
            diaryId: hoursDiaryModule.diaryDetail.nurseryHoursDiaryId,
            date: this.form.date,
            activity: this.form.activity ?? undefined,
            remarks: this.form.remarks ?? undefined,
            message: this.form.message ?? undefined,
            status: 'draft',
            staffIds: this.form.staff.length > 0 ? this.form.staff : undefined,
            appliedBy: this.form.appliedBy ?? this.currentSchool?.userId,
          };
          await hoursDiaryModule.updateHoursDiary(req);
        } else {
          const req: PostHoursDiaryRequest = {
            nurseryId: this.currentSchool?.nurseryId ?? -1,
            date: this.form.date,
            activity: this.form.activity ?? undefined,
            remarks: this.form.remarks ?? undefined,
            message: this.form.message ?? undefined,
            status: 'draft',
            staffIds: this.form.staff.length > 0 ? this.form.staff : undefined,
            appliedBy: this.form.appliedBy ?? this.currentSchool?.userId,
          };
          await hoursDiaryModule.postHoursDiary(req);
        }
        await this.loadDetail();
      } finally {
        this.isLoading = false;
      }
    },
    async saveAndApply() {
      this.isLoading = true;
      this.form.status = null;
      try {
        if (hoursDiaryModule.diaryDetail?.nurseryHoursDiaryId) {
          const req: ChangeHoursDiaryRequest = {
            nurseryId: this.currentSchool?.nurseryId ?? -1,
            date: this.form.date,
            diaryId: hoursDiaryModule.diaryDetail.nurseryHoursDiaryId,
            activity: this.form.activity ?? undefined,
            remarks: this.form.remarks ?? undefined,
            message: this.form.message ?? undefined,
            status: 'applied',
            staffIds: this.form.staff.length > 0 ? this.form.staff : undefined,
            appliedBy: this.form.appliedBy ?? this.currentSchool?.userId,
          };
          await hoursDiaryModule.updateHoursDiary(req);
        } else {
          const req: PostHoursDiaryRequest = {
            nurseryId: this.currentSchool?.nurseryId ?? -1,
            date: this.form.date,
            activity: this.form.activity ?? undefined,
            remarks: this.form.remarks ?? undefined,
            message: this.form.message ?? undefined,
            status: 'applied',
            staffIds: this.form.staff.length > 0 ? this.form.staff : undefined,
            appliedBy: this.form.appliedBy ?? this.currentSchool?.userId,
          };
          await hoursDiaryModule.postHoursDiary(req);
        }
        await this.loadDetail();
      } finally {
        this.isLoading = false;
      }
    },
    async changeApprovalStatus(approvalStatus: 'approved' | 'rejected') {
      if (approvalStatus === 'rejected' && this.form.status === 'approved') {
        const result = await Swal.fire({
          title: (this as any).$t('diary.confirmReject'),
          html: (this as any).$t('diary.willBeRejected'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: (this as any).$t('diary.reject'),
          cancelButtonText: (this as any).$t('diary.cancel'),
        });
        if (!result.isConfirmed) {
          return;
        }
      }

      this.isLoading = true;
      this.form.status = null;
      try {
        const req: ApproveHoursDiaryRequest = {
          nurseryId: this.currentSchool?.nurseryId ?? -1,
          diaryId: hoursDiaryModule.diaryDetail?.nurseryHoursDiaryId ?? -1,
          approvalStatus,
        };
        await hoursDiaryModule.approveHoursDiary(req);
        await this.loadDetail();
      } finally {
        this.isLoading = false;
      }
    }
  },
  mounted() {
    const nurseryHoursDairyDate = sessionStorage.getItem('nurseryHoursDairyDate');
    if (nurseryHoursDairyDate) this.date = dayjs(nurseryHoursDairyDate);
  }
});
</script>

<style scoped>
</style>
